import React from "react";
import { ChevronDown, Refresh } from "@bloomberg/media-ui-icons";
import styles from "./newsList.module.css";
import NewsListItem, { type NewsListItemTicker } from "./NewsListItem";
import NewsListItemSkeleton from "./NewsListItemSkeleton";
import Notification from "~components/Notification";

const SKELETON_COUNT = 20;

export interface NewsItem {
  readonly id: string;
  readonly category?: string;
  readonly headline: string;
  readonly imageUrl?: string;
  readonly tickers: readonly NewsListItemTicker[];
  readonly timestamp: string;
  readonly url?: string;
}

export interface NewsListProps {
  readonly items: readonly NewsItem[];
  readonly isLoading?: boolean;
  readonly error?: string | null;
  readonly onRetry?: () => void;
  readonly onBookmarkClick?: (id: string) => void;
  readonly hasActiveFilters?: boolean;
}

export default function NewsList({
  items,
  isLoading = false,
  error,
  onRetry,
  onBookmarkClick,
  hasActiveFilters = false,
}: NewsListProps) {
  if (isLoading) {
    return (
      <div className={styles.newsListContainer}>
        <div className={styles.newsList}>
          <div className={styles.resultsHeader}>
            <div />
            <div className={styles.sortContainer}>
              <span className={styles.sortLabel}>Sort by</span>
              <span className={styles.sortValue}>
                <span>Recent</span>
                <span className={styles.sortChevron}>
                  <ChevronDown />
                </span>
              </span>
            </div>
          </div>

          {Array.from({ length: SKELETON_COUNT }, (_, index) => (
            <NewsListItemSkeleton
              key={`news-skeleton-${index}`}
              showDivider={index < SKELETON_COUNT - 1}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.newsListContainer}>
        <div className={styles.errorContainer}>
          <Notification
            text={error}
            variant="default"
            title=""
            actionIcon={<Refresh />}
            actionText="Retry"
            onActionClick={onRetry}
          />
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    if (hasActiveFilters) {
      return (
        <div className={styles.emptyStateContainer}>
          <div className={styles.newsList}>
            <div className={styles.resultsHeader}>
              <div />
              <div className={styles.sortContainer}>
                <span className={styles.sortLabel}>Sort by</span>
                <span className={styles.sortValue}>
                  <span>Recent</span>
                  <span className={styles.sortChevron}>
                    <ChevronDown />
                  </span>
                </span>
              </div>
            </div>
            <output className={styles.emptyState}>
              <p className={styles.emptyStateTitle}>No matches found</p>
              <p className={styles.emptyStateSubtitle}>
                Adjust your filters to see relevant news.
              </p>
            </output>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div className={styles.newsListContainer}>
      <div className={styles.newsList}>
        <div className={styles.resultsHeader}>
          <div />
          <div className={styles.sortContainer}>
            <span className={styles.sortLabel}>Sort by</span>
            <span className={styles.sortValue}>
              <span>Recent</span>
              <span className={styles.sortChevron}>
                <ChevronDown />
              </span>
            </span>
          </div>
        </div>

        {items.map((item, index) => (
          <NewsListItem
            key={item.id}
            category={item.category}
            headline={item.headline}
            imageUrl={item.imageUrl}
            tickers={item.tickers}
            timestamp={item.timestamp}
            url={item.url}
            onBookmarkClick={
              onBookmarkClick ? () => onBookmarkClick(item.id) : undefined
            }
            showDivider={index < items.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
